import { Component, OnInit, inject } from '@angular/core';
import {ConsentService, ConsentStatus} from "../../../services/consent.service";
import {MatDialog} from "@angular/material/dialog";
import {TermsConsentDialogComponent} from "../../../components/person-contact/terms-consent-dialog/terms-consent-dialog.component";
import { AuthSessionService } from '../../../services/auth-session.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApiUrl } from '../../../shared/models/apiurl.models';
import { SystemApp } from '../../../constans/SystemConfigurations';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { LoadingMaskServiceService } from '../../../components/loading-frame/loading-frame.component';
import { Router } from '@angular/router';
import { LoginAction, LoginService } from '../../../components/session/login/login.service';
import { ToastService } from '../../../shared/toast/toast.service';
import { ProfileStateService } from '../profile-state.service';

interface UserInfo {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  mainPhone: number;
  scondryPhone: number;
  country: string;
  city: string;
  street: string;
  streetNumber: number;
  department: number;
  birthday: string;
  gender: string;
  idExDate: string;
  idNum: number;
  profilePhoto: string;
  neighborhood: string;
  zipCode: number;
  PoBox: number;
  area: string;
  FamilyStatus: string;
  roles: string;
  is_superuser: boolean;
  date_joined: string;
  last_login: string;
  avatar: string;
}

interface DdItem {
  label: string;
  value: string;
}

@Component({
    selector: 'app-info',
    templateUrl: './info.component.html',
    styleUrls: ['./info.component.css'],
    standalone: false
})
export class InfoComponent implements OnInit {
  private readonly authSession = inject(AuthSessionService);
  public href = '';
  // @ts-ignore
  userInfo: UserInfo = {};

  private options = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': this.apiKey.getJWTToken()
    })
  };

  base_url: string;

  // ── dropdown lists ─────────────────────────────────────────────
  cities:        DdItem[] = [];
  streets:       DdItem[] = [];
  neighborhoods: DdItem[] = [];
  areas:         DdItem[] = [];

  // input values (combined search + display)
  cityInput         = '';
  streetInput       = '';
  neighborhoodInput = '';
  areaInput         = '';

  // open/close state
  showCityDropdown         = false;
  showStreetDropdown       = false;
  showNeighborhoodDropdown = false;
  showAreaDropdown         = false;

  // loading flags
  loadingCities        = false;
  loadingStreets       = false;
  loadingNeighborhoods = false;
  loadingAreas         = false;

  // city API code needed to fetch streets
  selectedCityCode = '';

  // true while phone edit is open and not yet verified — disables save
  phoneEditPending = false;
  avatarChanged = false;

  readonly familyStatusOptions = ['רווק', 'נשוי/ה', 'גרוש/ה', 'אלמן/ה', 'פרוד/ה'];
  readonly genderOptions: DdItem[] = [
    { label: 'זכר',  value: 'Male'   },
    { label: 'נקבה', value: 'Female' },
    { label: 'אחר',  value: 'Other'  },
  ];

  // ── filtered getters ───────────────────────────────────────────
  get filteredCities() {
    return this.cityInput
      ? this.cities.filter(c => c.label.includes(this.cityInput))
      : this.cities;
  }
  get filteredStreets() {
    return this.streetInput
      ? this.streets.filter(s => s.label.includes(this.streetInput))
      : this.streets;
  }
  get filteredNeighborhoods() {
    return this.neighborhoodInput
      ? this.neighborhoods.filter(n => n.label.includes(this.neighborhoodInput))
      : this.neighborhoods;
  }
  get filteredAreas() {
    return this.areaInput
      ? this.areas.filter(a => a.label.includes(this.areaInput))
      : this.areas;
  }

  constructor(
    private http: HttpClient,
    private apiKey: ApiUrl,
    private loadingMaskServiceService: LoadingMaskServiceService,
    public translate: TranslateService,
    private router: Router,
    private loginService: LoginService,
    private toastService: ToastService,
    private profileState: ProfileStateService,
    private consentService: ConsentService,
    private dialog: MatDialog
  ) {
    this.base_url = SystemApp.base_url_api;
    this.loadData();
    this.loadConsent();
  }

  // ── SCRUM-124: terms acceptance (original date/time) + archive ──
  consent: ConsentStatus | null = null;
  consentBusy = false;
  consentMessage = '';

  loadConsent(): void {
    this.consentService.getStatus().subscribe({
      next: status => this.consent = status,
      error: () => this.consent = null
    });
  }

  get olderTermsAcceptances() {
    return (this.consent?.termsArchive || []).filter(a => !a.isCurrent);
  }

  acceptTermsFromProfile(): void {
    this.dialog.open(TermsConsentDialogComponent, {
      width: '760px', maxWidth: 'calc(100vw - 24px)', maxHeight: 'calc(100vh - 24px)',
      direction: 'rtl', autoFocus: false
    }).afterClosed().subscribe((accepted: boolean | undefined) => {
      if (!accepted) return;
      this.consentBusy = true;
      this.consentService.update({acceptTerms: true}).subscribe({
        next: status => { this.consent = status; this.consentBusy = false; },
        error: () => { this.consentBusy = false; this.consentMessage = 'לא הצלחנו לשמור את אישור התקנון. נסו שוב.'; }
      });
    });
  }

  /** Marketing (דיוור) opt-in / opt-out. Opt-out is always allowed. */
  toggleMarketing(checked: boolean): void {
    if (!this.consent) return;
    this.consentMessage = '';
    this.consentBusy = true;
    this.consentService.update({acceptAdv: checked}).subscribe({
      next: status => { this.consent = status; this.consentBusy = false; },
      error: err => {
        this.consentBusy = false;
        this.consentMessage = err?.error?.message || 'לא הצלחנו לשמור את הבחירה. נסו שוב.';
        this.loadConsent();
      }
    });
  }

  ngOnInit(): void {
    this.href = this.router.url;
  }

  // ── helpers ────────────────────────────────────────────────────
  private parseList(raw: any, cityCodeValue = false): DdItem[] {
    try {
      const rows: any[] = Array.isArray(raw) ? raw : (raw.results || raw.data || []);
      return rows
        .map(e => ({
          label: e.label?.toString() ?? '',
          value: cityCodeValue
            ? (e.value?.value?.toString() ?? e.label?.toString() ?? '')
            : (e.label?.toString() ?? '')
        }))
        .filter(e => e.label.trim().length > 0);
    } catch { return []; }
  }

  private apiGet(endpoint: string): Observable<any> {
    return this.http
      .get(`${this.base_url}/${endpoint}`, this.options)
      .pipe(map(res => res ?? null), catchError(err => throwError(err.error || err)));
  }

  private mapResponse(res: any): UserInfo {
    return {
      id:           res.id,
      username:     res.username,
      email:        res.email,
      first_name:   res.first_name,
      last_name:    res.last_name,
      mainPhone:    res.mainPhone,
      scondryPhone: res.scondryPhone,
      country:      res.country,
      city:         res.city,
      street:       res.street,
      streetNumber: res.streetNumber,
      department:   res.department,
      birthday:     res.birthday,
      gender:       res.gender,
      idExDate:     res.idExDate,
      idNum:        res.idNum,
      profilePhoto: res.profilePhoto,
      neighborhood: res.neighborhood,
      zipCode:      res.zipCode,
      PoBox:        res.PoBox,
      area:         res.area,
      FamilyStatus: res.FamilyStatus,
      roles:        res.roles,
      is_superuser: res.is_superuser,
      date_joined:  res.date_joined,
      last_login:   res.last_login,
      avatar:       res.avatar,
    };
  }

  // ── dropdown loaders ───────────────────────────────────────────
  private loadCitiesRaw(q = ''): void {
    this.loadingCities = true;
    const ep = q.trim()
      ? `tables/GetCities/?q=${encodeURIComponent(q.trim())}`
      : 'tables/GetCities/';
    this.apiGet(ep).subscribe({
      next:  res => { this.cities = this.parseList(res, true); this.loadingCities = false; },
      error: ()  => { this.loadingCities = false; }
    });
  }

  private loadStreetsRaw(cityCode: string, q = ''): void {
    if (!cityCode) return;
    this.loadingStreets = true;
    const base = `tables/GetStreets/?cityCode=${encodeURIComponent(cityCode)}`;
    const ep   = q.trim() ? `${base}&q=${encodeURIComponent(q.trim())}` : base;
    this.apiGet(ep).subscribe({
      next:  res => { this.streets = this.parseList(res); this.loadingStreets = false; },
      error: ()  => { this.loadingStreets = false; }
    });
  }

  private loadNeighborhoodsRaw(q = ''): void {
    // Neighborhood is a free-text field on the user model. There is no
    // neighborhood TableData lookup on the API, so keep the local list empty
    // instead of repeatedly requesting the unsupported `neighebrhood` table.
    this.loadingNeighborhoods = false;
    this.neighborhoods = [];
  }

  private loadAreasRaw(q = ''): void {
    this.loadingAreas = true;
    const base = 'api/table-data/?table_name=regions';
    const ep   = q.trim() ? `${base}&search=${encodeURIComponent(q.trim())}` : base;
    this.apiGet(ep).subscribe({
      next:  res => { this.areas = this.parseList(res); this.loadingAreas = false; },
      error: ()  => { this.loadingAreas = false; }
    });
  }

  // ── init dropdowns after user data loads ───────────────────────
  private initDropdowns(): void {
    // Cities — pre-search with saved value so it appears in the list
    this.loadingCities = true;
    const cityEp = this.userInfo.city
      ? `tables/GetCities/?q=${encodeURIComponent(this.userInfo.city)}`
      : 'tables/GetCities/';
    this.apiGet(cityEp).subscribe({
      next: res => {
        this.cities = this.parseList(res, true);
        this.loadingCities = false;
        const match = this.cities.find(c => c.label === this.userInfo.city);
        this.selectedCityCode = match?.value ?? this.userInfo.city ?? '';
        if (this.selectedCityCode) { this.initStreets(); }
      },
      error: () => { this.loadingCities = false; }
    });

    // Neighborhood remains editable as free text; the backend does not expose
    // a lookup table for it.
    this.neighborhoods = [];
    this.loadingNeighborhoods = false;

    // Areas
    this.loadingAreas = true;
    const areaEp = this.userInfo.area
      ? `api/table-data/?table_name=regions&search=${encodeURIComponent(this.userInfo.area)}`
      : 'api/table-data/?table_name=regions';
    this.apiGet(areaEp).subscribe({
      next:  res => { this.areas = this.parseList(res); this.loadingAreas = false; },
      error: ()  => { this.loadingAreas = false; }
    });
  }

  private initStreets(): void {
    this.loadingStreets = true;
    const base = `tables/GetStreets/?cityCode=${encodeURIComponent(this.selectedCityCode)}`;
    const ep   = this.userInfo.street
      ? `${base}&q=${encodeURIComponent(this.userInfo.street)}`
      : base;
    this.apiGet(ep).subscribe({
      next:  res => { this.streets = this.parseList(res); this.loadingStreets = false; },
      error: ()  => { this.loadingStreets = false; }
    });
  }

  // ── input change handlers ──────────────────────────────────────
  onCityInput(val: string): void {
    this.cityInput = val;
    this.showCityDropdown = true;
    if (val.length > 1) { this.loadCitiesRaw(val); }
  }

  onCitySelect(item: DdItem): void {
    this.userInfo.city    = item.label;
    this.cityInput        = item.label;
    this.selectedCityCode = item.value;
    this.showCityDropdown = false;
    // reset street
    this.userInfo.street  = '';
    this.streetInput      = '';
    this.streets          = [];
    if (this.selectedCityCode) { this.loadStreetsRaw(this.selectedCityCode); }
  }

  onStreetInput(val: string): void {
    this.streetInput = val;
    this.showStreetDropdown = true;
    if (val.length > 1 && this.selectedCityCode) {
      this.loadStreetsRaw(this.selectedCityCode, val);
    }
  }

  onStreetSelect(item: DdItem): void {
    this.userInfo.street    = item.label;
    this.streetInput        = item.label;
    this.showStreetDropdown = false;
  }

  onNeighborhoodInput(val: string): void {
    this.neighborhoodInput = val;
    this.showNeighborhoodDropdown = true;
    if (val.length > 1) { this.loadNeighborhoodsRaw(val); }
  }

  onNeighborhoodSelect(item: DdItem): void {
    this.userInfo.neighborhood    = item.label;
    this.neighborhoodInput        = item.label;
    this.showNeighborhoodDropdown = false;
  }

  onAreaInput(val: string): void {
    this.areaInput = val;
    this.showAreaDropdown = true;
    if (val.length > 1) { this.loadAreasRaw(val); }
  }

  onAreaSelect(item: DdItem): void {
    this.userInfo.area    = item.label;
    this.areaInput        = item.label;
    this.showAreaDropdown = false;
  }

  // close with small delay so mousedown on option fires first
  hideDropdown(which: 'city' | 'street' | 'neighborhood' | 'area'): void {
    setTimeout(() => {
      if (which === 'city')         { this.showCityDropdown         = false; }
      if (which === 'street')       { this.showStreetDropdown       = false; }
      if (which === 'neighborhood') { this.showNeighborhoodDropdown = false; }
      if (which === 'area')         { this.showAreaDropdown         = false; }
    }, 150);
  }

  // ── save / load ────────────────────────────────────────────────
  saveUserInfo(): void {
    const avatarWasChanged = this.avatarChanged;
    this.loadingMaskServiceService.setOnLoadingMask(true, true);
    this.http.put(this.apiKey.getUserInfo(), this.userInfo, this.options)
      .pipe(map(res => res ?? null), catchError(err => throwError(err.error || err)))
      .subscribe({
        next: res => {
          this.userInfo = this.mapResponse(res);
          if (avatarWasChanged && this.userInfo.avatar) {
            this.userInfo.avatar = this.cacheBust(this.userInfo.avatar);
          }
          this.avatarChanged = false;
          this.profileState.publish(this.userInfo);
          this.loadingMaskServiceService.setOnLoadingMask(false, false);
          this.toastService.success('הפרופיל עודכן בהצלחה', {
            message: avatarWasChanged ? 'תמונת הפרופיל החדשה נשמרה' : undefined
          });
        },
        error: (err: any) => {
          this.loadingMaskServiceService.setOnLoadingMask(false, false);
          this.toastService.error('לא הצלחנו לעדכן את הפרופיל', { message: err?.message || 'נסו שוב בעוד רגע' });
        }
      });
  }

  loadData(): void {
    this.loadingMaskServiceService.setOnLoadingMask(true, true);
    this.http.get(this.apiKey.getUserInfo(), this.options)
      .pipe(map(res => res ?? null), catchError(err => throwError(err.error || err)))
      .subscribe({
        next: (res:any) => {
          this.userInfo         = this.mapResponse(res);
          this.cityInput        = res.city         ?? '';
          this.streetInput      = res.street        ?? '';
          this.neighborhoodInput = res.neighborhood ?? '';
          this.areaInput        = res.area           ?? '';
          this.loadingMaskServiceService.setOnLoadingMask(false, false);
          this.initDropdowns();
        },
        error: () => { this.loadingMaskServiceService.setOnLoadingMask(false, false); }
      });
  }

  onAvatarChange(image: string): void {
    this.userInfo.avatar = image;
    this.avatarChanged = true;
  }

  private cacheBust(url: string): string {
    if (!url || url.startsWith('data:')) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}v=${Date.now()}`;
  }

  logout() {
    // POD1-7: full logout - revoke the refresh token server-side and drop
    // the socket, not just clear the local flag.
    this.authSession.logout().subscribe();
    this.loginService.sendClickEvent(LoginAction.Close);
  }

  openLogin() { this.loginService.sendClickEvent(LoginAction.Open); }
}
